import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { ServerResourceMetrics } from '@kcdash/shared';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { LineChart as ChartIcon, Cpu, HardDrive, Network, Users } from 'lucide-react';

export const MetricsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [metrics, setMetrics] = useState<ServerResourceMetrics[]>([]);
  const [range, setRange] = useState<'1h' | '24h' | '7d'>('1h');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadMetrics() {
      if (!id) return;
      setIsLoading(true);
      try {
        const data = await api.getMetrics(id, range);
        setMetrics(data);
      } catch (err) {
        console.error('Failed to load metrics:', err);
      } finally {
        setIsLoading(false);
      }
    }

    loadMetrics();
    const interval = setInterval(loadMetrics, 30000);
    return () => clearInterval(interval);
  }, [id, range]);

  const chartData = metrics.map((m) => ({
    time: new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    cpu: m.cpuPercent,
    ram: Math.round(m.memoryBytes / (1024 * 1024)),
    netRx: Math.round(m.networkRxBytesPerSec / 1024),
    netTx: Math.round(m.networkTxBytesPerSec / 1024),
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-neutral-100">Telemetry &amp; Resource Metrics</h2>
          <p className="text-xs text-neutral-400 mt-1 font-mono">
            Sampled every 30 seconds • Stored efficiently in PostgreSQL
          </p>
        </div>

        <div className="flex items-center gap-1 bg-panel-card border border-panel-border p-1 rounded-lg text-xs font-mono">
          {(['1h', '24h', '7d'] as const).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-md transition ${
                range === r ? 'bg-brand-600 text-white font-medium' : 'text-neutral-400 hover:text-neutral-200'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {isLoading && metrics.length === 0 ? (
        <div className="py-20 text-center text-neutral-500 font-mono text-sm">
          Loading metrics history...
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* CPU Chart */}
          <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Cpu className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-semibold text-neutral-200">CPU Utilization (%)</h3>
            </div>
            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="cpuColor" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="time" stroke="#4b5563" fontSize={11} tickLine={false} />
                  <YAxis stroke="#4b5563" fontSize={11} tickLine={false} domain={[0, 100]} />
                  <Tooltip contentStyle={{ backgroundColor: '#131816', borderColor: '#202b26', borderRadius: '8px' }} />
                  <Area type="monotone" dataKey="cpu" stroke="#10b981" fill="url(#cpuColor)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* RAM Chart */}
          <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <HardDrive className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-semibold text-neutral-200">RAM Usage (MB)</h3>
            </div>
            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="ramColor" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="time" stroke="#4b5563" fontSize={11} tickLine={false} />
                  <YAxis stroke="#4b5563" fontSize={11} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#131816', borderColor: '#202b26', borderRadius: '8px' }} />
                  <Area type="monotone" dataKey="ram" stroke="#3b82f6" fill="url(#ramColor)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Network Traffic Chart */}
          <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Network className="w-4 h-4 text-purple-400" />
              <h3 className="text-sm font-semibold text-neutral-200">Network I/O Rate (KB/s)</h3>
            </div>
            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <XAxis dataKey="time" stroke="#4b5563" fontSize={11} tickLine={false} />
                  <YAxis stroke="#4b5563" fontSize={11} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#131816', borderColor: '#202b26', borderRadius: '8px' }} />
                  <Line type="monotone" dataKey="netRx" name="RX (KB/s)" stroke="#a855f7" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="netTx" name="TX (KB/s)" stroke="#ec4899" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
